/**
 * Pure GPS-trail clean-up, shared by the `gettriplocations` callable (which
 * cleans before it thins a trail down for the wire) and the web Journey page
 * (which runs it again over whatever it was handed, so trails cached on a
 * phone before this existed get the same treatment).
 *
 * No imports on purpose: the web app pulls this file in directly, so it has to
 * compile under both the Functions and the app tsconfig.
 */

export interface TrailPoint {
  lat: number;
  lng: number;
  /** epoch milliseconds */
  t: number;
  src: string;
}

/** A stretch where someone stayed within a small radius for a while. */
export interface Stay {
  /** Index of the first and last point of the stay in the array it was found in. */
  startIndex: number;
  endIndex: number;
  lat: number;
  lng: number;
  startT: number;
  endT: number;
}

const EARTH_RADIUS_M = 6_371_000;
const RAD = Math.PI / 180;

export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const dLat = (b.lat - a.lat) * RAD;
  const dLng = (b.lng - a.lng) * RAD;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

// ---------------------------------------------------------------------------
// One device at a time
// ---------------------------------------------------------------------------

// Phone, watch and the web tab all write into the same trail. Two receivers a
// few metres apart, interleaved, draw a zig-zag — so where a better source has
// a point nearby in time, the others' points are dropped. The watch wins: it's
// on your wrist (a phone gets left on a table or in a bag) and its GPS is tuned
// for run tracking. Then the phone, then the web tab, which is a phone's GPS
// read through a browser.
const SOURCE_RANK: Record<string, number> = { watch: 0, phone: 1, web: 2, legacy: 3 };
const SOURCE_OVERLAP_MS = 90_000;

function sourceRank(src: string): number {
  return SOURCE_RANK[src] ?? 2;
}

/** True if sorted `times` holds a value within `windowMs` of `t`. */
function hasTimeNear(times: number[], t: number, windowMs: number): boolean {
  let lo = 0;
  let hi = times.length;
  const from = t - windowMs;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid]! < from) lo = mid + 1;
    else hi = mid;
  }
  return lo < times.length && times[lo]! <= t + windowMs;
}

/** Drops points from a lesser source wherever a better one was recording. Input sorted by `t`. */
export function preferOneSource(points: TrailPoint[]): TrailPoint[] {
  const timesByRank = new Map<number, number[]>();
  for (const p of points) {
    const r = sourceRank(p.src);
    let list = timesByRank.get(r);
    if (!list) timesByRank.set(r, (list = []));
    list.push(p.t);
  }
  if (timesByRank.size < 2) return points.slice();
  const ranks = [...timesByRank.keys()].sort((a, b) => a - b);
  return points.filter((p) => {
    const r = sourceRank(p.src);
    for (const better of ranks) {
      if (better >= r) break;
      if (hasTimeNear(timesByRank.get(better)!, p.t, SOURCE_OVERLAP_MS)) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// Wi-Fi and cell fixes
// ---------------------------------------------------------------------------

/**
 * Drops fixes whose exact coordinates turn up again later, with other fixes
 * in between. GPS never lands on the same spot to the tenth of a metre twice;
 * a location from Wi-Fi or a cell tower does, because it's the position on
 * file for that access point. They're what the trail falls back to indoors —
 * on a dark ride, in a hotel — and they put you wherever the access point is
 * listed, so the trail flicks between a handful of fixed spots. On a real
 * trip (Disneyland, a fix a minute) about one point in ten was one, and they
 * made most of the jumps across the park.
 *
 * The same fix repeated back-to-back is left alone: that's a device
 * re-reporting while standing still.
 */
export function dropRecurringFixes(points: TrailPoint[]): TrailPoint[] {
  const seen = new Map<string, number>();
  const recurring = new Set<string>();
  points.forEach((p, i) => {
    const key = `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;
    const last = seen.get(key);
    if (last !== undefined && i - last > 1) recurring.add(key);
    seen.set(key, i);
  });
  if (recurring.size === 0) return points.slice();
  return points.filter((p) => !recurring.has(`${p.lat.toFixed(6)},${p.lng.toFixed(6)}`));
}

// ---------------------------------------------------------------------------
// Out-and-back spikes
// ---------------------------------------------------------------------------

// The signature of a bad fix: a sudden jump away, then a return to roughly
// where it left from shortly after. Walking can't produce it — 5 m/s is a fast
// run — and real travel at speed doesn't come straight back.
const SPIKE_MIN_JUMP_M = 75;
const SPIKE_MIN_SPEED_MPS = 5;
/**
 * The return has to land within this fraction of the jump from where it left.
 * Half, not less: with a fix a minute, you've walked on a little by the time
 * the GPS comes back, so the return rarely lands right where it left.
 */
const SPIKE_RETURN_RATIO = 0.5;
/** How long a run of bad fixes can last and still count as one excursion. */
const SPIKE_MAX_EXCURSION_MS = 3 * 60_000;
const SPIKE_MAX_RUN = 20;

/** Removes GPS excursions that jump away and come straight back. Input sorted by `t`. */
export function dropSpikes(points: TrailPoint[]): TrailPoint[] {
  if (points.length < 3) return points.slice();
  const out: TrailPoint[] = [points[0]!];
  let i = 1;
  while (i < points.length) {
    const a = out[out.length - 1]!;
    const b = points[i]!;
    const jump = distanceMeters(a, b);
    const seconds = Math.max(1, b.t - a.t) / 1000;
    if (jump >= SPIKE_MIN_JUMP_M && jump / seconds >= SPIKE_MIN_SPEED_MPS) {
      let back = -1;
      for (let k = i + 1; k < points.length && k <= i + SPIKE_MAX_RUN; k++) {
        const c = points[k]!;
        if (c.t - b.t > SPIKE_MAX_EXCURSION_MS) break;
        if (distanceMeters(a, c) <= jump * SPIKE_RETURN_RATIO) {
          back = k;
          break;
        }
      }
      if (back !== -1) {
        // Everything from the jump up to (not including) the return was the excursion.
        i = back;
        continue;
      }
    }
    out.push(b);
    i++;
  }
  return out;
}

/** Sorts, de-duplicates timestamps, drops Wi-Fi/cell fixes, picks one source, and drops spikes. */
export function cleanTrail(points: TrailPoint[]): TrailPoint[] {
  const sorted = points
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && Number.isFinite(p.t))
    .slice()
    .sort((a, b) => a.t - b.t);
  const unique: TrailPoint[] = [];
  for (const p of sorted) {
    if (unique.length > 0 && unique[unique.length - 1]!.t === p.t) continue;
    unique.push(p);
  }
  return dropSpikes(preferOneSource(dropRecurringFixes(unique)));
}

// ---------------------------------------------------------------------------
// Stays
// ---------------------------------------------------------------------------

/** GPS wanders 10–40 m standing still, more indoors. */
export const STAY_RADIUS_M = 50;
export const STAY_MIN_MS = 10 * 60_000;
/** A stay survives this many consecutive fixes wandering outside the radius. */
const STAY_STRAY_POINTS = 2;

/**
 * Finds stretches where the trail stays within [STAY_RADIUS_M] of its running
 * centre for at least [STAY_MIN_MS]. A stay may span a stretch with no data at
 * all (the phone off overnight) as long as it picks up where it left off.
 */
export function findStays(
  points: TrailPoint[],
  radiusM = STAY_RADIUS_M,
  minMs = STAY_MIN_MS,
): Stay[] {
  const stays: Stay[] = [];
  let i = 0;
  while (i < points.length) {
    const first = points[i]!;
    let sumLat = first.lat;
    let sumLng = first.lng;
    let n = 1;
    let end = i;
    let j = i + 1;
    while (j < points.length) {
      const centre = { lat: sumLat / n, lng: sumLng / n };
      const p = points[j]!;
      if (distanceMeters(centre, p) <= radiusM) {
        sumLat += p.lat;
        sumLng += p.lng;
        n++;
        end = j;
        j++;
        continue;
      }
      // A fix or two wandering off doesn't end the stay if the next is back.
      let back = -1;
      for (let k = j + 1; k < points.length && k <= j + STAY_STRAY_POINTS; k++) {
        if (distanceMeters(centre, points[k]!) <= radiusM) {
          back = k;
          break;
        }
      }
      if (back === -1) break;
      j = back;
    }
    const last = points[end]!;
    if (last.t - first.t >= minMs) {
      stays.push({
        startIndex: i,
        endIndex: end,
        lat: sumLat / n,
        lng: sumLng / n,
        startT: first.t,
        endT: last.t,
      });
      i = end + 1;
    } else {
      i++;
    }
  }
  return stays;
}

/**
 * Replaces each stay's points with two — arriving and leaving — at its centre,
 * so a night in a hotel is one dot rather than a scribble.
 */
export function collapseStays(points: TrailPoint[], stays: Stay[]): TrailPoint[] {
  if (stays.length === 0) return points.slice();
  const out: TrailPoint[] = [];
  let next = 0;
  for (const stay of stays) {
    for (let k = next; k < stay.startIndex; k++) out.push(points[k]!);
    const src = points[stay.startIndex]!.src;
    out.push({ lat: stay.lat, lng: stay.lng, t: stay.startT, src });
    if (stay.endT > stay.startT) out.push({ lat: stay.lat, lng: stay.lng, t: stay.endT, src });
    next = stay.endIndex + 1;
  }
  for (let k = next; k < points.length; k++) out.push(points[k]!);
  return out;
}

// ---------------------------------------------------------------------------
// Thinning
// ---------------------------------------------------------------------------

/** Consecutive points further apart than this are a gap in the data, not a line walked. */
export const GAP_MS = 10 * 60_000;

/**
 * Douglas–Peucker in space *and* time: a point goes only if it lies within
 * `toleranceM` of where straight-line travel between its neighbours would have
 * put it at that moment. So a straight walk thins to its ends, while corners,
 * stops and changes of pace survive. Indices in `pinned` are always kept.
 */
export function simplifyTrail(
  points: TrailPoint[],
  toleranceM: number,
  pinned: Iterable<number> = [],
): TrailPoint[] {
  const n = points.length;
  if (n <= 2) return points.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  for (const idx of pinned) if (idx >= 0 && idx < n) keep[idx] = 1;

  const stack: [number, number][] = [];
  let prev = 0;
  for (let k = 1; k < n; k++) {
    if (keep[k]) {
      stack.push([prev, k]);
      prev = k;
    }
  }
  while (stack.length > 0) {
    const [s, e] = stack.pop()!;
    if (e - s < 2) continue;
    const a = points[s]!;
    const b = points[e]!;
    const span = b.t - a.t;
    let worst = -1;
    let worstIdx = -1;
    for (let k = s + 1; k < e; k++) {
      const p = points[k]!;
      const f = span > 0 ? (p.t - a.t) / span : 0;
      const expected = { lat: a.lat + (b.lat - a.lat) * f, lng: a.lng + (b.lng - a.lng) * f };
      const d = distanceMeters(expected, p);
      if (d > worst) {
        worst = d;
        worstIdx = k;
      }
    }
    if (worst > toleranceM) {
      keep[worstIdx] = 1;
      stack.push([s, worstIdx], [worstIdx, e]);
    }
  }
  return points.filter((_, k) => keep[k] === 1);
}

const SIMPLIFY_TOLERANCES_M = [3, 6, 12, 25, 50, 100, 200, 400];

/**
 * Everything the server does to a raw trail before sending it: clean it,
 * collapse stays, then thin with the smallest tolerance that fits `maxPoints`.
 * Stay ends and the edges of data gaps are never thinned away.
 */
export function prepareTrail(points: TrailPoint[], maxPoints: number): TrailPoint[] {
  const cleaned = cleanTrail(points);
  const collapsed = collapseStays(cleaned, findStays(cleaned));

  const pinned: number[] = [];
  for (let k = 1; k < collapsed.length; k++) {
    const a = collapsed[k - 1]!;
    const b = collapsed[k]!;
    // A collapsed stay is two points at one spot; a gap is a long silence.
    if (b.t - a.t >= GAP_MS || (a.lat === b.lat && a.lng === b.lng)) pinned.push(k - 1, k);
  }

  let out = collapsed;
  for (const tolerance of SIMPLIFY_TOLERANCES_M) {
    out = simplifyTrail(collapsed, tolerance, pinned);
    if (out.length <= maxPoints) return out;
  }
  // Still too long (weeks of driving): fall back to an even spread.
  const step = Math.ceil(out.length / maxPoints);
  return out.filter((_, k) => k % step === 0 || k === out.length - 1);
}
